import React, { useRef, useState } from 'react';
import { useBreakpoints } from '@fiverr-private/business_blocks';
import { useEnteredView } from '@fiverr-private/hooks';
import { I18n } from '@fiverr-private/i18n-react';
import { Container, Stack } from '@fiverr-private/layout_components';
import { logger } from '@fiverr-private/obs';
import { Carousel } from '@fiverr-private/orca';
import { atoms, Box } from '@fiverr-private/theme';
import { Heading, Text } from '@fiverr-private/typography';
import { ErrorBoundary } from '@fiverr-private/ui_utils';
import { bigQuery, EVENT_TYPE, PAGE_NAME } from '../../monitoring/bigQuery';
import { mixpanel } from '../../monitoring/mixpanel';
import { CustomOrderButton } from '../CustomOrder/CustomOrderButton';
import { CustomOrderModal } from '../CustomOrder/CustomOrderModal';
import { StickyHeader } from '../StickyHeader';
import { CONTACT_BUTTON_POSITIONS } from '../../monitoring/bigQuery/createContactButtonEventEnrichment';
import { AgencyService } from '../../types';
import { ItemModal } from './ItemModal';
import { PreviewItem } from './PreviewItem';
import { HourlyRatesInfo } from './HourlyRatesInfo';

import { carouselClass, carouselSlideClass, servicesContainerMdWidth } from './styles.ve.css';

const onRenderError = (error: any) => {
    logger.error(new Error('Failed while rendering the AgencyServices Component'), { cause: error });
};

export interface AgencyServicesProps {
    agencyServices: AgencyService[] | null;
}

const AgencyServices = ({ agencyServices }: AgencyServicesProps) => {
    const observableRef = useRef<HTMLDivElement>(null);
    const { isSM } = useBreakpoints();
    const [serviceIndexForModal, setServiceIndexForModal] = useState<number | null>(null);
    const slidesToShow = isSM ? 1 : 2;
    const isServiceModalOpen = Number.isInteger(serviceIndexForModal);

    useEnteredView(observableRef, {
        threshold: 1,
        onEntered: () => {
            if (!agencyServices) return;
            bigQuery.impressionEvent(EVENT_TYPE.AGENCY_SERVICES_IS_SHOWN, {
                agency: {
                    navigation_button_exist: carouselSlides.length > slidesToShow,
                    services: agencyServices.map((service) => service.serviceName),
                },
            });
            mixpanel.event('Services viewed');
        },
    });

    if (!agencyServices) {
        return null;
    }

    const getOnSeeMoreClickHandler = (serviceIndex: number) => () => setServiceIndexForModal(serviceIndex);

    const handleServiceModalClose = () => setServiceIndexForModal(null);

    const handleNextServiceForModalClick =
        !isServiceModalOpen || serviceIndexForModal === agencyServices.length - 1
            ? null
            : () => setServiceIndexForModal((serviceIndexForModal as number) + 1);

    const handlePrevServiceForModalClick =
        !isServiceModalOpen || serviceIndexForModal === 0
            ? null
            : () => setServiceIndexForModal((serviceIndexForModal as number) - 1);

    const carouselSlides = agencyServices.map((service, index) => ({
        item: service,
        onSeeMoreClick: getOnSeeMoreClickHandler(index),
    }));

    const handleArrowClick = () => {
        bigQuery.clickEvent(EVENT_TYPE.CLICKED_ON_NAVIGATION_IN_SERVICES, {
            agency: {
                navigation_button_exist: carouselSlides.length > slidesToShow,
                services: agencyServices.map((service) => service.serviceName),
            },
        });
    };

    return (
        <ErrorBoundary onError={onRenderError}>
            <CustomOrderModal />
            {isServiceModalOpen && (
                <ItemModal
                    showMoreExist={carouselSlides.length > slidesToShow}
                    item={agencyServices[serviceIndexForModal as number]}
                    itemIndex={serviceIndexForModal as number}
                    onClose={handleServiceModalClose}
                    onNextItem={handleNextServiceForModalClick}
                    onPrevItem={handlePrevServiceForModalClick}
                />
            )}
            <Box ref={observableRef} id={StickyHeader.TabName.Services}>
                <Stack
                    direction={{
                        default: 'column',
                        md: 'row',
                        sm: 'column',
                    }}
                    gap="12"
                >
                    <Container width={{ default: '100%', md: `${servicesContainerMdWidth}px` }} flexShrink="0">
                        <Heading as="h5" size="h_xxl" marginBottom="4">
                            <I18n k="agency_page_perseus.services_we_provide" />
                        </Heading>
                        <Text
                            size="b_lg"
                            marginBottom={{
                                default: '4',
                                md: '6',
                            }}
                        >
                            <I18n k="agency_page_perseus.services_we_provide_description" />
                        </Text>
                        <HourlyRatesInfo marginBottom="6" />
                        <CustomOrderButton biSource={PAGE_NAME} position={CONTACT_BUTTON_POSITIONS.SERVICE_MODAL} />
                    </Container>
                    <Carousel
                        onArrowClick={handleArrowClick}
                        slidesToShow={slidesToShow}
                        slides={carouselSlides}
                        Component={PreviewItem}
                        componentProps={{ showMoreExist: carouselSlides.length > slidesToShow }}
                        carouselClassName={carouselClass}
                        slideClassName={carouselSlideClass}
                        arrowsClassName={atoms({
                            boxShadow: 'aa',
                        })}
                    />
                </Stack>
            </Box>
        </ErrorBoundary>
    );
};

/** Default export to support lazyLoad imports  */
export default AgencyServices;
